import React, { useCallback, useEffect } from "react";
import sharedStyles from "../Shared.module.scss";
import styles from "../campaignOrders/CampaignOrders.module.scss";
import { strings } from "../../../../../../intl/fa";
import OrderedAdItem, {
  adStateLabels,
  useOpenOrderDetails,
} from "./orderedAdItem/OrderedAdItem";
import AdEmptyState from "./AdEmptyState/AdEmptyState";
import {
  Button,
  ButtonSize,
  Download,
  NewButton,
  NewButtonSize,
  PlusSquare,
} from "@bale/ui";
import { useAppDispatch, useAppSelector } from "../../../../../../utils/store";
import { useRequest } from "../../../../../../hooks/useRequest";
import { getPanelAdsThunk } from "../../../../../../core/ordersList/ordersList.thunk";
import {
  NavigationActions,
  AppPage,
  AppPageUrls,
} from "../../../../../../core/navigation/navigation.slice";
import { useNavigate, useLocation } from "react-router-dom";
import { AdsListShimmer, OrderItemsList } from "../Shared";
import { useOrdersListState } from "../orderTabs.utils";
import classNames from "classnames";
import {
  OrderAdActions,
  OrderAdPages,
} from "../../../../../../core/orderAd/orderAd.slice";
import { sendYandexGoal, YandexGoal } from "../../../../../../yandex/yandex";
import { useTheme } from "@emotion/react";
import Footer from "components/commons/footer/Footer";
import { exportToExcel, showAdDate } from "utils/utils";
import { AdState } from "core/orderAd/orderAd.entity";
import { percentCalc, toMoneyFormat } from "@bale/utils";
import useIsLargeScreen from "../../hooks/useIsDesktop";

type Props = {
  disableButton: boolean;
};

export const AdOrders: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { sendRequest: getAds, loading } = useRequest(getPanelAdsThunk);
  const ordersList = useAppSelector((state) => state.ordersList.orders);
  const dispatch = useAppDispatch();
  const search = useLocation().search;
  const navigate = useNavigate();
  const selectAd = useOpenOrderDetails();
  const { showDivider, showEmptyState, showShimmer, showList, showChildren } =
    useOrdersListState(loading, ordersList, getAds);
  const isDesktop = useIsLargeScreen(1400);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const id = params.get("id");
    if (id) {
      const order = ordersList.find((order) => order.id === id);
      if (order) {
        navigate(AppPageUrls[AppPage.OrdersList], { replace: true });
        selectAd(order);
      }
    }
  }, [ordersList]);

  const openSubmitAdPage = useCallback(() => {
    dispatch(NavigationActions.setAppPage(AppPage.SubmitAd));
    dispatch(OrderAdActions.setPage(OrderAdPages.SelectAdType));
    sendYandexGoal(YandexGoal.CreateAdOrderBtn);
  }, []);

  const theme = useTheme();

  const exportHandler = () => {
    const exportingAdData = ordersList
      .filter(
        (ad) => ad.state === AdState.Active || ad.state === AdState.Finished
      )
      .map((ad) => {
        const ctr = toMoneyFormat(percentCalc(ad.click_count, ad.view_count));
        return {
          [strings.ad_excel.id]: ad.id,
          [strings.ad_excel.title]: ad.title,
          [strings.ad_excel.description]: ad.description,
          [strings.ad_excel.link]: ad.link,
          [strings.ad_excel.view_count]: ad.view_count,
          [strings.ad_excel.click_count]: ad.click_count,
          [strings.ad_excel.start_time]: showAdDate(ad.start_time),
          [strings.ad_excel.state]: adStateLabels[ad.state]?.label,
          [strings.ad_excel.ctr]: ctr,
        };
      });
    exportToExcel(exportingAdData, "ad-report", "ads");
  };

  return (
    <>
      {showChildren && props.children && (
        <div
          className={classNames(sharedStyles.ChildrenWrapper, {
            [sharedStyles.hasDivider]: showDivider,
          })}
        >
          {props.children}
        </div>
      )}
      {showShimmer && <AdsListShimmer />}
      {showEmptyState && (
        <AdEmptyState isButtonDisabled={props.disableButton} />
      )}
      {showList && (
        <>
          <OrderItemsList>
            <div className={styles.ButtonsContainer}>
              <NewButton
                size={NewButtonSize.MEDIUM}
                data-testid="add-ad-button"
                icon={PlusSquare}
                className={sharedStyles.AddButton}
                onClick={openSubmitAdPage}
              >
                {strings.ad_sale.add}
              </NewButton>
              <div className={styles.DownloadCampaign} onClick={exportHandler}>
                <Download size={24} color={theme.colors.primary[50]} />
                <span>{strings.ad_sale.download_ads_report}</span>
              </div>
            </div>
            <div
              className={styles.list}
              style={{ flexDirection: isDesktop ? "row" : undefined }}
            >
              {isDesktop && (
                <div className={styles.tableHeader}>
                  <p className={styles.col}>
                    {strings.ad_sale.ordered_ads_list.adImage}
                  </p>
                  <p className={styles.col}>
                    {strings.dialog_ad_form.info_step.title_label}
                  </p>
                  <p className={styles.col}>
                    {strings.dialog_ad_form.info_step.text_label}
                  </p>
                  <p className={styles.col}>
                    {strings.ad_sale.ordered_ads_list.adSpot}
                  </p>
                  <p className={styles.col}>
                    {strings.ad_sale.ordered_ads_list.adStatus}
                  </p>
                  <p className={styles.col}>
                    {strings.ad_sale.ordered_ads_list.clickOrView}
                  </p>
                  <p className={styles.col}>
                    {strings.ad_sale.ordered_ads_list.more}
                  </p>
                </div>
              )}
              {ordersList.map((order) => (
                <OrderedAdItem adRecord={order} key={order.id} />
              ))}
            </div>
          </OrderItemsList>
          <Footer mobileOnly>
            <NewButton
              text={strings.ad_sale.add}
              style={{ width: "100%", margin: "0 16px" }}
              size={NewButtonSize.MEDIUM}
              onClick={openSubmitAdPage}
              disabled={props.disableButton}
              icon={PlusSquare}
            />
          </Footer>
        </>
      )}
    </>
  );
};
